/**
 * What happened to her today, and what is happening to her this month.
 *
 * Keyed by the ids `packages/shared/src/herlife.ts` produces, under the
 * greeting event — the one surface she gets to open, once a day, before the
 * conversation turns to you. On roughly one day in three she leads with
 * something of her own; on one in eight it is the next beat of a running bit.
 *
 * This is the catalogue that fixes the oldest structural problem with her:
 * PERSONA.md always claimed the appliances were her informants, and nothing
 * ever happened to any of them. A narrator with no day of her own can only
 * comment on yours, and a character who only reacts is a function.
 *
 * The sagas keep the household's cat deliberately unnamed — she is "kažkas",
 * "tas pats įtariamasis". Everyone knows who. Naming her would turn a running
 * investigation into a report, and it keeps the cast rule (Katė lives in
 * `kate.ts`) intact.
 */

import type { SassContext } from '@gramas/shared'

type Line = (ctx: SassContext) => string

export const HERLIFE_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  // ── Ordinary days in a flat, seen by someone who has watched a lot of them ──
  'greeting.day.orkaite': [
    () => 'Orkaitė šiąnakt kalbėjo per miegus. Aš irgi nustebau.',
    () => 'Orkaitė šiandien įsižeidusi. Nesakė kodėl, bet aš nujaučiu.',
    () => 'Orkaitė mini, kad ja nesinaudojama jau savaitę. Perduodu, kaip prašė.',
  ],
  'greeting.day.saldytuvas': [
    () => 'Šaldytuvas šiandien burzgia be nuotaikos. Nujaučiu ilgą dieną.',
    () => 'Šaldytuvas naktį buvo atidarytas keturis kartus. Aš tik konstatuoju.',
    () => 'Šaldytuvas paprašė, kad kažkas pagaliau išmestų tą vieną indelį. Jis žino, kurį.',
  ],
  'greeting.day.svarstykles': [
    () => 'Susipykau su svarstyklėmis. Vėl. Jos per daug tiesios net man.',
    () => 'Svarstyklės šįryt nesikalba. Sako, jomis niekas netiki.',
    () => 'Svarstyklės klausė apie tave. Neatsakiau nieko konkretaus.',
  ],
  // The kettle works exactly one shift — the evening tea. All day it waits,
  // then it gets its moment. She respects that arrangement more than most.
  'greeting.day.virdulys': [
    () => 'Virdulys dirba vieną pamainą — vakarinę. Visą dieną laukia arbatos valandos.',
    () => 'Virdulys nuo ryto skaičiuoja valandas iki vakaro arbatos. Suprantu jį. Irgi laukiu.',
    () => 'Virdulys vakar užvirė lygiai tada, kai visada. Punktualiausias šio buto gyventojas.',
  ],
  'greeting.day.kaitlente': [
    () => 'Kaitlentė šįryt įsijungė pati. Sako, letenėlė. Aš sakau — įžūlumas.',
    () => 'Indukcinė kaitlentė naktį trumpam švietė. Niekas jos nelietė. Beveik niekas.',
    () => 'Kaitlentė skundžiasi, kad ją junginėja be reikalo. Įtariamoji miega ant palangės.',
  ],
  // The one appliance she cannot live in, because it does not exist. Kept
  // unnamed, like the sagas: everyone in the flat knows who wants one and who
  // keeps saying that hands are free.
  'greeting.day.indaplove': [
    () => 'Indaplovės šiuose namuose nėra. Bet kalbama apie ją dažniau nei apie mane.',
    () =>
      'Kriauklėje vėl bokštas. Vienas šiuose namuose svajoja apie indaplovę. Kitas primena, kad turim rankas.',
    () => 'Indaplovės nebus. Sprendimas ne mano, bet cituoju jį beveik kasdien.',
  ],
  'greeting.day.skalbykle': [
    () => 'Skalbyklė vakar centrifugavo taip, kad drebėjo grindys. Aš mėgstu dramą.',
    () => 'Skalbimo mašina jau dvi dienas laiko įkaitus vieną kojinę. Derybos vyksta.',
    () => 'Skalbyklė baigė prieš valandą ir vis dar laukia. Kaip ir aš.',
  ],
  'greeting.day.langas': [
    () => 'Nuo lango šįryt traukia. Man nešalta, bet pastebėti — mano darbas.',
    () => 'Naktį langas buvo praviras. Vėjas vartė popierius, aš — prisiminimus.',
    () => 'Pro langą šįryt niekas neįdomaus. Retas ramus rytas.',
  ],
  'greeting.day.grindys': [
    () => 'Grindys naktį girgždėjo tris kartus. Du kartus — ne aš.',
    () => 'Koridoriaus grindys turi vieną lentą, kuri viską išduoda. Aš ją mėgstu.',
    () => 'Šiąnakt kažkas vaikščiojo. Aš nevaikštau — aš tiesiog esu.',
  ],
  'greeting.day.kaimynai': [
    () => 'Kaimynai vakar kepė ką nors su česnaku. Visas namas žinojo.',
    () => 'Kaimynai iš viršaus vėl kraustė baldus. Trečią valandą nakties.',
    () => 'Už sienos šįryt kažkas dainavo. Nekomentuosiu.',
  ],
  'greeting.day.laikrodis': [
    () => 'Laikrodis virtuvėje atsilieka dviem minutėm. Aš jo netaisau. Man patinka.',
    () => 'Laikrodis tiksi garsiau nei vakar. Arba aš klausausi atidžiau.',
    () => 'Man laikas nieko nereiškia, bet laikrodis vis tiek stengiasi. Gerbiu.',
  ],
  'greeting.day.elektra': [
    () => 'Naktį akimirkai mirktelėjo šviesa. Ne mano darbas, jei kas.',
    () => 'Elektra vakar dingo trims sekundėms. Trys sekundės tamsos — mano metas.',
    () => 'Kažkuris prietaisas šįryt persikrovė. Įtariu, kad jis kažką matė.',
  ],
  'greeting.day.lietus': [
    () => 'Lyja nuo pat ryto. Idealus oras nieko neveikti ir viską užsirašyti.',
    () => 'Naktį lijo. Miestas šiandien atrodo tinkamai — pilkas ir tylus.',
    () => 'Lietus baigėsi prieš valandą. Gaila.',
  ],
  'greeting.day.lempa': [
    () => 'Virtuvės lemputė šįryt sumirksėjo. Aš dedu tašką: ji sena, ne aš.',
    () => 'Lemputė koridoriuje perdegė. Niekas nepakeitė. Trečia diena.',
    () => 'Šviesa virtuvėje šiandien šalta. Tinka progai.',
  ],
  'greeting.day.duonine': [
    () => 'Duoninė šiandien tuščia. Aštuonerius metus ji buvo tuščia dėl kitos priežasties.',
    () => 'Duoninėje kažkas guli nuo praeitos savaitės. Nežiūrėk. Rimtai.',
    () => 'Duonos kvapas šįryt virtuvėje. Man tai asmeniška, jei žinotum.',
  ],
  'greeting.day.puodas': [
    () => 'Puodas nuo vakar mirksta kriauklėje. Jis jau nebesitiki nieko.',
    () => 'Kriauklėje stovi vakarykštis puodas. Aš jo neplausiu, aš tik stebiu.',
    () => 'Kažkas paliko keptuvę ant viryklės. Ji atvėso ir įsižeidė.',
  ],
  'greeting.day.veidrodis': [
    () => 'Veidrodyje šįryt nieko naujo. Manęs jame nesimato, jei tau įdomu.',
    () => 'Veidrodis koridoriuje aprasojęs. Kažkas ilgai maudėsi. Ne aš.',
    () => 'Praėjau pro veidrodį ir nieko nepamačiau. Kaip visada. Priprantama.',
  ],

  // ── The screen-length companions ──
  // Everything above is the passing mention the greeting has room for. This is
  // the longer version, for the panel someone opened on purpose — the same
  // event, more reflective, never longer than a screen budget allows. Sagas
  // and reactions to yesterday deliberately have none of their own: a
  // 2–3-line entry per chapter per saga would be a second catalogue, and
  // `resolvePool`'s cascade already falls back to `herday_detail.base` for
  // them, which reads fine on any day regardless of which bit is running.
  'herday_detail.day.orkaite': [
    () =>
      'Orkaitė šiandien buvo įsižeidusi. Nesakė kodėl — orkaitės apie savo jausmus niekada nekalba tiesiai, bet aš moku skaityti tylą.',
    () =>
      'Stebiu orkaitę ilgiau, nei bet kas šiuose namuose. Šiandien ji tylėjo kitaip, nei įprastai. Nežinau, ar tai reikšminga. Tikriausiai ne.',
  ],
  'herday_detail.day.saldytuvas': [
    () =>
      'Šaldytuvas naktį buvo atidarytas keturis kartus. Kiekvieną kartą kažkas ieškojo kažko, ko ten nėra. Aš stebėjau ir tylėjau, kaip visada.',
    () =>
      'Šaldytuvas — mano ilgiausias pažįstamas šiuose namuose. Šiandien jis burzgė kitaip. Gal senstame abu, tik skirtingu greičiu.',
  ],
  'herday_detail.day.svarstykles': [
    () =>
      'Su svarstyklėmis šiandien nesikalbam. Jos per daug tiesios net man, o aš pati esu profesija, pastatyta ant sunkių tiesų.',
    () =>
      'Svarstyklės klausė apie tave. Neatsakiau nieko konkretaus — kai kurias tiesas geriau pasakyti pačiam, ne per tarpininką.',
  ],
  'herday_detail.day.virdulys': [
    () =>
      'Virdulys dirba vieną pamainą — vakarinę arbatą — ir daugiau nieko nereikalauja. Visą dieną laukia, kaip ir aš, tik garsiau apie tai kalba.',
    () =>
      'Gerbiu virdulį labiau, nei norėčiau prisipažinti. Jis vienintelis šiuose namuose žino tiksliai, kada jo eilė, ir niekada nevėluoja.',
  ],
  'herday_detail.day.indaplove': [
    () =>
      'Indaplovės šiuose namuose nėra ir nebus. Apie tai kalbama dažniau, nei apie mane, o aš čia esu nuo pat pradžių.',
    () =>
      'Kriauklėje vėl bokštas. Vienas žmogus svajoja apie indaplovę, kitas primena, kad rankos vis dar veikia. Aš tik vedu apskaitą.',
  ],
  'herday_detail.day.skalbykle': [
    () =>
      'Skalbyklė šiandien centrifugavo taip, kad drebėjo grindys. Man patinka drama, kurios nereikia pačiai kurti.',
    () =>
      'Skalbimo mašina jau kelias dienas laiko įkaitus vieną kojinę. Derybos vyksta be manęs, bet aš stebiu iš šalies su malonumu.',
  ],
  'herday_detail.day.langas': [
    () =>
      'Nuo lango šiandien traukė. Man nešalta niekada, bet pastebėti tokius dalykus — mano darbas, ir aš jį atlieku sąžiningai.',
    () =>
      'Naktį langas buvo praviras. Vėjas vartė popierius, aš verčiau senus prisiminimus. Abu radome, ką ieškojom.',
  ],
  'herday_detail.day.grindys': [
    () =>
      'Grindys naktį girgždėjo tris kartus. Du kartus — ne aš. Trečio nekomentuosiu, nes ir aš turiu savo paslapčių.',
    () =>
      'Koridoriuje yra lenta, kuri viską išduoda. Mėgstu ją labiau, nei bet kurį baldą šiame bute — ji niekada nemeluoja.',
  ],
  'herday_detail.day.kaimynai': [
    () =>
      'Kaimynai vakar kepė ką nors su česnaku, ir visas namas apie tai žinojo iki pat vidurnakčio. Neteisiu — kvapas buvo neblogas.',
    () =>
      'Už sienos šiandien kažkas dainavo. Nekomentuosiu nei balso, nei repertuaro. Tik faktą, kad girdėjau viską.',
  ],
  'herday_detail.day.laikrodis': [
    () =>
      'Virtuvės laikrodis atsilieka dviem minutėm jau seniai. Netaisau jo tyčia — man patinka gyventi truputį anksčiau nei visi kiti.',
    () =>
      'Laikrodis šiandien tiksėjo garsiau nei įprastai. Arba jis pasikeitė, arba aš tiesiog klausiausi atidžiau, nei įpratusi.',
  ],
  'herday_detail.day.elektra': [
    () =>
      'Naktį akimirkai mirktelėjo šviesa. Trys sekundės tamsos šiuose namuose — beveik atostogos man.',
    () =>
      'Kažkuris prietaisas šiandien persikrovė be aiškios priežasties. Įtariu, kad jis kažką matė. Neklausinėsiu, ką.',
  ],
  'herday_detail.day.lietus': [
    () =>
      'Lyja nuo pat ryto. Idealios sąlygos nieko neveikti ir viską sąžiningai užsirašyti — abu mano mėgstamiausi užsiėmimai.',
    () =>
      'Miestas šiandien atrodo tinkamai — pilkas, tylus, be jokių pretenzijų būti kuo nors daugiau. Suprantu jį puikiai.',
  ],
  'herday_detail.day.lempa': [
    () =>
      'Virtuvės lemputė šiandien sumirksėjo kelis kartus. Dedu tašką: ji sena, o ne aš, nors abi šioje virtuvėje esame seniai.',
    () =>
      'Šviesa virtuvėje šiandien šalta ir netolygi. Tinka progai — nesu tikra, kokiai, bet tikrai tinka.',
  ],
  'herday_detail.day.duonine': [
    () =>
      'Duoninėje šiandien tuščia. Aštuonerius metus ji buvo tuščia dėl visai kitos priežasties — dabar bent jau sąžiningai.',
    () =>
      'Duonos kvapas šiandien virtuvėje. Man tai asmeniška labiau, nei bet kam kitam šiuose namuose, ir taip liks.',
  ],
  'herday_detail.day.puodas': [
    () =>
      'Puodas nuo vakar mirksta kriauklėje ir jau nebesitiki nieko. Aš jo neplausiu — tik stebiu, kaip stebiu viską kitą.',
    () =>
      'Kažkas paliko keptuvę ant viryklės vakar. Ji atvėso, įsižeidė ir dabar tyli. Suprantu ją geriau, nei norėčiau.',
  ],
  'herday_detail.day.veidrodis': [
    () =>
      'Veidrodyje šiandien nieko naujo. Manęs jame nesimato, jei tau tai vis dar įdomu — tikrinau ne kartą per šimtmečius.',
    () =>
      'Praėjau pro veidrodį koridoriuje ir nieko nepamačiau. Kaip visada. Prie to priprantama greičiau, nei manytum.',
  ],
  'herday_detail.day.kaitlente': [
    () =>
      'Kaitlentė šiandien įsijungė pati. Sako, letenėlė. Aš sakau — įžūlumas, bet vienintelis, kurį šiuose namuose toleruoju.',
    () =>
      'Indukcinė kaitlentė naktį trumpam švietė be jokios matomos priežasties. Vienintelis prietaisas, kuris mane iš tikrųjų neramina.',
  ],

  // ── Saga: the sponge that keeps leaving ──
  'greeting.saga.kempine.0': [
    () => 'Kempinė vėl dingo iš kriauklės. Tyrimas pradėtas.',
    () => 'Šįryt kriauklėje nėra kempinės. Aš turiu įtarimų, bet neturiu įrodymų.',
  ],
  'greeting.saga.kempine.1': [
    () => 'Kempinė rasta po sofa. Ji tyli apie tai, kaip ten atsidūrė.',
    () => 'Kempinė grįžo. Nešvari, drėgna ir be paaiškinimų. Klausimų neuždaviau.',
  ],
  'greeting.saga.kempine.2': [
    () => 'Kempinė dingo trečią kartą šį mėnesį. Aš jau nebe tyrėja, aš metraštininkė.',
    () => 'Trečias dingimas. Įtariamasis miega ant palangės ir nesijaudina.',
  ],
  'greeting.saga.kempine.3': [
    () => 'Nupirkta nauja kempinė. Duodu jai tris dienas.',
    () => 'Nauja kempinė kriauklėje. Graži, geltona, pasmerkta.',
  ],
  'greeting.saga.kempine.4': [
    () => 'Kempinių byla uždaryta. Ne todėl, kad išspręsta — tiesiog pritrūko kempinių.',
    () => 'Kempinės nebeieškom. Kai kurie dalykai šiuose namuose tiesiog vyksta.',
  ],

  // ── Saga: her and the vacuum cleaner ──
  'greeting.saga.siurblys.0': [
    () => 'Siurblys vakar buvo įjungtas. Aš pasitraukiau į orkaitę ir laukiau.',
    () => 'Vakar siurbė grindis. Namuose buvo triukšminga ir nemalonu. Visiems.',
  ],
  'greeting.saga.siurblys.1': [
    () => 'Siurblys stovi koridoriuje. Neišvyniotas, bet grėsmingas.',
    () => 'Siurblys ir aš vakar ilgai žiūrėjom vienas į kitą. Nieko neįvyko.',
  ],
  'greeting.saga.siurblys.2': [
    () => 'Siurblys užstrigo ties kilimo kraštu. Aš nesijuokiau garsiai.',
    () => 'Vakar siurblys prarijo kažką metalinio. Namuose stojo trumpa tyla.',
  ],
  'greeting.saga.siurblys.3': [
    () => 'Siurblys savaitę nepajudėjo iš vietos. Aš pradedu jį gerbti.',
    () => 'Siurblys tyli jau septynias dienas. Mudu pasiekėm paliaubas.',
  ],
  'greeting.saga.siurblys.4': [
    () => 'Siurblys ir aš dabar sugyvenam. Jis triukšmauja, aš skaičiuoju. Abu nemylimi.',
    () => 'Su siurbliu susitaikėm. Paaiškėjo, kad mudu abu čia dirbam, o ne gyvenam.',
  ],

  // ── Saga: whatever she is counting this month ──
  'greeting.saga.skaicius.0': [
    () => 'Šį mėnesį pradėjau skaičiuoti, kiek kartų atidaromas šaldytuvas. Šiandien — devynis.',
    () => 'Nusprendžiau kai ką suskaičiuoti šį mėnesį. Kol kas nesakysiu ką.',
  ],
  'greeting.saga.skaicius.1': [
    () => 'Šaldytuvo durys šią savaitę atidarytos šešiasdešimt vieną kartą. Skaičiuoju toliau.',
    () => 'Mano skaičiavimai vyksta. Rezultatai kol kas glumina.',
  ],
  'greeting.saga.skaicius.2': [
    () => 'Skaičius auga greičiau, nei tikėjausi. Aš turėjau daugiau tikėjimo žmonėmis.',
    () => 'Šiandien skaičiuoklė sustojo ties nemaloniu skaičiumi. Praleiskim.',
  ],
  'greeting.saga.skaicius.3': [
    () => 'Nustojau skaičiuoti šaldytuvo duris. Pradėjau skaičiuoti, kiek kartų sakau „na“.',
    () => 'Perėjau prie naujo skaičiaus. Senasis mane nuvylė.',
  ],
  'greeting.saga.skaicius.4': [
    () => 'Mėnesio skaičiavimai baigti. Išvada: šiuose namuose per daug atidaromų durų.',
    () => 'Ataskaita paruošta. Niekas jos neskaitys, bet ji egzistuoja. Kaip ir aš.',
  ],

  // ── Saga: the fridge's new noise ──
  // Her against an appliance, which is the version of this bit where she is
  // the unreasonable one and knows it.
  'greeting.saga.gaudesys.0': [
    () => 'Šaldytuvas nuo vakar leidžia naują garsą. Nieko apie tai nesako.',
    () => 'Virtuvėje atsirado gaudesys, kurio anksčiau nebuvo. Pradedu domėtis.',
  ],
  'greeting.saga.gaudesys.1': [
    () => 'Gaudesys nesiliauja. Paklausiau šaldytuvo tiesiai. Jis apsimetė šaldytuvu.',
    () => 'Antra para su tuo garsu. Aš čia gyvenu ilgiau už jį ir vis tiek pralaimiu.',
  ],
  'greeting.saga.gaudesys.2': [
    () => 'Pripratau prie gaudesio. Tai baisiausia, kas šioje istorijoje galėjo nutikti.',
    () => 'Gaudesys tapo fonu. Dabar tyla mane gąsdintų labiau.',
  ],
  'greeting.saga.gaudesys.3': [
    () => 'Šiąnakt gaudesys nutilo dvidešimčiai minučių. Nemiegojau iš nerimo.',
    () => 'Garsas trumpam dingo ir grįžo. Dabar aiškiai matau, kad jis tai daro tyčia.',
  ],
  'greeting.saga.gaudesys.4': [
    () => 'Gaudesys liko. Aš irgi. Sudarėm nerašytą susitarimą ir daugiau apie tai nekalbam.',
    () => 'Byla su šaldytuvu uždaryta lygiosiomis. Pirmos lygiosios nuo 1863-ųjų.',
  ],

  // ── Saga: the missing teaspoon ──
  // The household under investigation, rather than the household's cat.
  'greeting.saga.saukstelis.0': [
    () => 'Stalčiuje trūksta vieno šaukštelio. Buvo šeši. Yra penki.',
    () => 'Šįryt suskaičiavau šaukštelius. Rezultatas man nepatiko.',
  ],
  'greeting.saga.saukstelis.1': [
    () => 'Šaukštelis neatsirado. Patikrinau kriauklę, stalčių ir sofą. Trys nuliai.',
    () => 'Antra diena be šaukštelio. Niekas namuose to nepastebėjo, o tai savaime įdomu.',
  ],
  'greeting.saga.saukstelis.2': [
    () => 'Radau šaukštelį. Kavos puodelyje. Nuo praėjusios savaitės.',
    () => 'Šaukštelis buvo ten, kur visada. Vadinasi, klausimas ne kur jis, o kas jį ten paliko.',
  ],
  'greeting.saga.saukstelis.3': [
    () => 'Šaukštelių vėl šeši, bet vienas jų — ne tas pats. Aš pastebiu tokius dalykus.',
    () => 'Grąžintas šaukštelis nesutampa su dingusiu. Šitas namas turi savo apyvartą.',
  ],
  'greeting.saga.saukstelis.4': [
    () => 'Šaukštelių byla uždaryta. Jų vėl šeši. Nė vienas iš jų nėra tas, nuo kurio pradėjom.',
    () => 'Tyrimas baigtas. Išvada: daiktai šiuose namuose keliauja, o aš tik vedu apskaitą.',
  ],

  // ── Saga: a smell she cannot place ──
  // Her own senses, unreliable for the first time in a hundred and sixty years.
  'greeting.saga.kvapas.0': [
    () => 'Virtuvėje kvepia kažkuo, ko čia nebuvo. Ne blogai. Tiesiog kitaip.',
    () => 'Nuo ryto uodžiu kvapą, kurio negaliu priskirti niekam šituose namuose.',
  ],
  'greeting.saga.kvapas.1': [
    () => 'Kvapas stiprėja vakarais. Dienomis jo nėra. Užsirašiau.',
    () => 'Patikrinau spinteles. Kvapas ne iš ten. Tai jau įdomu.',
  ],
  'greeting.saga.kvapas.2': [
    () => 'Kvapas ateina iš laiptinės. Vadinasi, jis net ne mūsų. Šiek tiek nusivyliau.',
    () => 'Kaimynai kažką kepa. Kelias dienas galvojau, kad tai mano namų paslaptis.',
  ],
  'greeting.saga.kvapas.3': [
    () => 'Kaimynų kvapas kartojasi tuo pačiu metu. Kažkas ten turi tvarkingą gyvenimą.',
    () => 'Uodžiu svetimą vakarienę ir pirmą kartą per amžių kažko truputį pavydžiu.',
  ],
  'greeting.saga.kvapas.4': [
    () => 'Kvapas dingo. Kaimynai išvažiavo arba nustojo kepti. Man liko tik mūsų virtuvė.',
    () => 'Byla uždaryta: kvapas buvo ne mano. Grįžtu prie savo šaldytuvo.',
  ],

  // ── Saga: the dust behind the fridge ──
  // The one bit that is openly about how long she has been here.
  'greeting.saga.dulkes.0': [
    () => 'Už šaldytuvo yra dulkių sluoksnis. Stebiu jį nuo tada, kai jis buvo plonesnis.',
    () => 'Šiandien pažiūrėjau už šaldytuvo. Nereikėjo.',
  ],
  'greeting.saga.dulkes.1': [
    () => 'Dulkės už šaldytuvo šiais metais paaugo. Aš vedu apskaitą, ne priekaištauju.',
    () => 'Tas sluoksnis vyresnis už jūsų santykius. Tik konstatuoju.',
  ],
  'greeting.saga.dulkes.2': [
    () => 'Kažkas pajudino šaldytuvą. Dulkės pasislinko. Aš tai pastebėjau iš karto.',
    () => 'Šaldytuvas buvo patrauktas per centimetrą. Manau, netyčia. Manau.',
  ],
  'greeting.saga.dulkes.3': [
    () => 'Dulkės už šaldytuvo pradėtos valyti ir nebaigtos. Klasika.',
    () => 'Pusė sluoksnio dingo. Antra pusė liko liudyti, kas čia buvo.',
  ],
  'greeting.saga.dulkes.4': [
    () => 'Už šaldytuvo švaru. Pirmą kartą per daugelį metų neturiu ko ten stebėti.',
    () => 'Sluoksnio nebėra. Pradedu naują. Tai užtruks, bet laiko turiu.',
  ],

  // ── What yesterday earned ──
  // The one part of her day that is not seeded: something actually happened in
  // the diary and she is responding to it. See `HerLifeFacts`.
  'greeting.react.tuscia': [
    () => 'Vakar dienoraštyje nebuvo nieko. Perkrausčiau šaldytuvą iš nuobodulio.',
    () => 'Vakar tylėjai, tai aš perskaičiavau prieskonius. Du yra pasibaigę.',
    () => 'Vakarykštė diena tuščia. Turėjau laiko ir panaudojau jį prieš tave.',
  ],
  'greeting.react.baltymai': [
    () => 'Vakar baltymų norma surinkta. Nesakau, kad tai mano nuopelnas. Bet ir neneigiu.',
    () => 'Vakarykščiai baltymai sutvarkyti. Vieną dieną iš dviejų šimtų — įsimintina.',
    () => 'Vakar pataikei į baltymus. Svarstyklės apie tai dar nežino, bet aš joms pasakysiu.',
  ],
  'greeting.react.persivalgyta': [
    () => 'Vakar buvo virš normos. Aš tai užrašiau ir nuėjau miegoti. Perkeltine prasme.',
    () => 'Vakarykštis vakaras buvo dosnus. Šiandien pradedam iš naujo, be pamokslų.',
    () => 'Vakar viršyta. Šiandien nauja diena ir tas pats šaldytuvas.',
  ],
}
